/**
 * The descriptive sheet of a deposit: what the stored analysis read and measured, slide by
 * slide, with its uncertainties (docs/DEPOSIT_ANALYSIS.md, « Fiche descriptive »). It is
 * computed in the browser from the analysis document already kept in deposit_analyses: nothing
 * is measured again, nothing is guessed. It does not interpret the story or the intent (hook,
 * role of a slide, call to action): those stay with the members.
 *
 * Every field read from the document is optional, so an older analysis (0.1.0, 0.2.0) shows what
 * it has and says what it lacks.
 */

/** The parts of the analysis document (packages/schemas/carousel-analysis.schema.json) the sheet reads. */
export interface AnalysisDocument {
  schema_version?: string;
  generated_at?: string;
  analyzer?: { version?: string };
  slides?: AnalysisDocumentSlide[];
  limits?: string[];
}

export interface AnalysisDocumentLine {
  text?: string;
  kind?: string;
  kind_uncertain?: boolean;
  kind_reasons?: string[];
  score?: number;
  low_confidence?: boolean;
  suspect?: boolean;
  unexpected_script?: boolean;
  numeric?: boolean;
  repeated_on?: number;
  box?: number[];
  band?: string;
  height_ratio?: number;
  on_block?: boolean;
  light_block?: boolean;
  coloured?: { role?: string; hex?: string; inside_hex?: string | null } | null;
  colors?: { text_hex?: string; background_hex?: string; contrast_l?: number; uncertain?: boolean };
}

export interface AnalysisDocumentHypothesis {
  id?: string;
  statement?: string;
  verdict?: string;
  reliability?: string;
  note?: string;
  evidence?: Record<string, unknown>;
}

export interface AnalysisDocumentSlide {
  position?: number | null;
  role?: string;
  status?: string;
  error?: string | null;
  text?: { status?: string; error?: string; lines?: AnalysisDocumentLine[] };
  measures?: {
    image?: { width?: number; height?: number };
    composition?: { status?: string; axis?: string | null; position?: number | null };
    dominant_colors?: Array<{ hex: string; share: number }>;
    light?: { bottom_minus_middle_l?: number };
    circles_lower_right?: Array<{ ring_hex?: string }>;
    flat_rectangles?: unknown[];
  };
  hypotheses?: AnalysisDocumentHypothesis[];
}

export type Verdict = "consistent" | "uncertain" | "inconsistent" | "not_measured";

export interface FicheLine {
  text: string;
  /** The analysis's own category: editorial (added on top), brand, in_photo (the scene), doubtful. */
  kind: string;
  kindLabel: string;
  uncertain: boolean;
  reasons: string[];
  /** OCR confidence, 0..1, when recorded. */
  score: number | null;
  /** Top and bottom of the line, in % of the image height (0 = top). */
  top: number | null;
  bottom: number | null;
  /** Height of the letters, in % of the image height. */
  height: number | null;
  band: string | null;
  numbering: boolean;
  repeatedOn: number | null;
  textHex: string | null;
  backgroundHex: string | null;
  contrast: number | null;
  coloured: { role: "block" | "letters" | string; hex: string; insideHex: string | null } | null;
  /** Doubts the analysis recorded about this line, in words. */
  flags: string[];
}

export interface FicheDetection {
  id: string;
  label: string;
  /** The reference statement it was compared with (ZOO, a particular reference, never imposed). */
  statement: string;
  verdict: Verdict | string;
  verdictLabel: string;
  reliability: string | null;
  note: string | null;
  /** The measured facts behind the verdict, in words. */
  facts: string[];
}

export interface FicheSlide {
  position: number | null;
  analysed: boolean;
  error: string | null;
  textStatus: string;
  textError: string | null;
  size: [number, number] | null;
  lines: FicheLine[];
  composition: { status: string; axis: string | null; position: number | null } | null;
  dominantColors: Array<{ hex: string; share: number }>;
  bottomMinusMiddle: number | null;
  ochreBlocks: Array<{ text: string; hex: string; deltaE: number | null; insideHex: string | null }>;
  detections: FicheDetection[];
  uncertainties: string[];
}

export interface Fiche {
  analyzerVersion: string;
  generatedAt: string | null;
  slides: FicheSlide[];
  linesByKind: Record<string, number>;
  /** The same text read at the same place on several slides. */
  repeated: Array<{ text: string; positions: number[] }>;
  /** List numbers the analysis recognised, slide by slide. */
  numbering: Array<{ position: number; text: string }>;
  /** What this version of the analysis could not record (older versions). */
  missing: string[];
  limits: string[];
}

export const KIND_LABELS: Record<string, string> = {
  editorial: "ajouté par-dessus",
  brand: "marque",
  in_photo: "dans la photo",
  doubtful: "douteux",
};

export const VERDICT_LABELS: Record<string, string> = {
  consistent: "cohérent avec la référence",
  uncertain: "incertain",
  inconsistent: "contraire à la référence",
  not_measured: "non mesuré",
};

export const RELIABILITY_LABELS: Record<string, string> = { high: "élevée", medium: "moyenne", low: "faible", none: "aucune" };

const DETECTION_LABELS: Record<string, string> = {
  "zoo.logo_top": "Logo OPULANCIA en haut",
  "zoo.headline_bottom": "Titre en capitales en bas",
  "zoo.ochre_blocks": "Blocs ocre",
  "zoo.circled_arrow": "Cercle en bas à droite",
  "zoo.bottom_darkening": "Bas de l'image plus sombre",
  "zoo.full_bleed_photo": "Image plein cadre",
  "zoo.font": "Police",
};

/** Limits found by the evaluation of 10 October 2026 (docs/VISUAL_EVAL.md), shown with every sheet. */
export const KNOWN_LIMITS = [
  "Un texte imprimé dans la photo (ticket, emballage, enseigne) peut être classé « ajouté par-dessus », et l'inverse.",
  "Un bloc ocre est rapporté à toute la ligne lue, pas au mot qu'il porte.",
  "Deux images fondues l'une dans l'autre, sans coupure nette, sont vues comme une seule image.",
  "Un bouton ou une carte d'interface (par exemple « Suivre ») peut être classé « dans la photo ».",
  "La police n'est pas identifiée ; les couleurs sont mesurées sur les fichiers déposés, pas sur la publication.",
];

const NUMBERING = "numérotation de liste";
const pct = (v: number | undefined) => (typeof v === "number" ? Math.round(v * 1000) / 10 : null);
const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);
const str = (v: unknown) => (typeof v === "string" && v ? v : null);

function line(x: AnalysisDocumentLine): FicheLine {
  const kind = x.kind ?? "unknown";
  const reasons = (x.kind_reasons ?? []).filter((r) => typeof r === "string");
  const flags: string[] = [];
  if (x.kind_uncertain) flags.push("catégorie incertaine");
  if (x.low_confidence) flags.push("lecture peu sûre");
  if (x.suspect) flags.push("lecture suspecte");
  if (x.unexpected_script) flags.push("écriture inattendue");
  if (x.colors?.uncertain) flags.push("couleurs incertaines");
  const box = Array.isArray(x.box) && x.box.length === 4 ? x.box : null;
  const coloured = x.coloured && typeof x.coloured.hex === "string" ? { role: x.coloured.role ?? "?", hex: x.coloured.hex, insideHex: x.coloured.inside_hex ?? null } : null;
  return {
    text: x.text ?? "",
    kind,
    kindLabel: KIND_LABELS[kind] ?? kind,
    uncertain: Boolean(x.kind_uncertain),
    reasons,
    score: num(x.score),
    top: box ? pct(box[1]) : null,
    bottom: box ? pct(box[3]) : null,
    height: pct(x.height_ratio),
    band: x.band ?? null,
    numbering: reasons.some((r) => r.includes(NUMBERING)),
    repeatedOn: num(x.repeated_on),
    textHex: str(x.colors?.text_hex),
    backgroundHex: str(x.colors?.background_hex),
    contrast: num(x.colors?.contrast_l),
    coloured,
    flags,
  };
}

function facts(h: AnalysisDocumentHypothesis): string[] {
  const e = h.evidence ?? {};
  const out: string[] = [];
  if (h.id === "zoo.logo_top" && typeof e["text"] === "string") out.push(`lu : « ${e["text"]} »${typeof e["similarity"] === "number" ? ` (ressemblance ${Math.round(e["similarity"] * 100)} %)` : ""}`);
  if (h.id === "zoo.circled_arrow" && typeof e["circles"] === "number") out.push(`${e["circles"]} cercle(s) détecté(s) en bas à droite`);
  if (h.id === "zoo.bottom_darkening" && typeof e["bottom_minus_middle_l"] === "number") out.push(`luminosité du bas moins celle du milieu : ${e["bottom_minus_middle_l"]}`);
  if (h.id === "zoo.full_bleed_photo" && typeof e["edge_density"] === "number") out.push(`densité de contours ${e["edge_density"]}, ${e["large_flat_areas"] ?? 0} grand(s) aplat(s)`);
  if (h.id === "zoo.headline_bottom" && typeof e["bottom_lines"] === "number") out.push(`${e["bottom_lines"]} ligne(s) en bas, dont ${e["uppercase_lines"] ?? 0} en capitales`);
  return out;
}

function ochre(h: AnalysisDocumentHypothesis | undefined): FicheSlide["ochreBlocks"] {
  const blocks = h?.evidence?.["blocks_in_lines"];
  if (!Array.isArray(blocks)) return [];
  return blocks
    .filter((b): b is Record<string, unknown> => typeof b === "object" && b !== null && typeof (b as Record<string, unknown>)["hex"] === "string")
    .map((b) => ({ text: String(b["text"] ?? ""), hex: String(b["hex"]), deltaE: num(b["delta_e"]), insideHex: str(b["inside_hex"]) }));
}

function slide(s: AnalysisDocumentSlide): FicheSlide {
  const analysed = s.status === "analyzed";
  const textStatus = s.text?.status ?? (analysed ? "unknown" : "not_run");
  const lines = (s.text?.lines ?? []).map(line).sort((a, b) => (a.top ?? 0) - (b.top ?? 0));
  const hyps = s.hypotheses ?? [];
  const detections: FicheDetection[] = hyps
    .filter((h) => typeof h.id === "string")
    .map((h) => ({
      id: h.id!,
      label: DETECTION_LABELS[h.id!] ?? h.id!,
      statement: h.statement ?? "",
      verdict: h.verdict ?? "not_measured",
      verdictLabel: VERDICT_LABELS[h.verdict ?? ""] ?? h.verdict ?? "—",
      reliability: h.reliability ? RELIABILITY_LABELS[h.reliability] ?? h.reliability : null,
      note: h.note ?? null,
      facts: facts(h),
    }));
  const comp = s.measures?.composition;
  const composition = comp?.status ? { status: comp.status, axis: comp.axis ?? null, position: num(comp.position) } : null;
  const blocks = ochre(hyps.find((h) => h.id === "zoo.ochre_blocks"));

  const uncertainties: string[] = [];
  if (!analysed) uncertainties.push(`Slide non analysée${s.error ? ` : ${s.error}` : ""}.`);
  if (analysed && textStatus === "failed") uncertainties.push(`Lecture du texte en échec${s.text?.error ? ` : ${s.text.error}` : ""} ; les mesures visuelles restent.`);
  if (analysed && textStatus === "not_run") uncertainties.push("Texte non lu (OCR non exécuté).");
  if (analysed && textStatus === "read" && lines.length === 0) uncertainties.push("Aucune ligne lue : cela ne prouve pas l'absence de texte.");
  const uncertainKind = lines.filter((l) => l.uncertain).length;
  if (uncertainKind) uncertainties.push(`${uncertainKind} ligne(s) de catégorie incertaine.`);
  const unsure = lines.filter((l) => l.flags.some((f) => f.startsWith("lecture") || f === "écriture inattendue")).length;
  if (unsure) uncertainties.push(`${unsure} lecture(s) peu sûre(s), suspecte(s) ou d'écriture inattendue.`);
  if (composition?.status === "not_detected") uncertainties.push("Aucune coupure nette détectée : deux images fondues l'une dans l'autre resteraient invisibles à la mesure.");
  if (blocks.length) uncertainties.push("Blocs ocre rapportés à toute la ligne lue, pas au mot.");
  for (const d of detections) {
    if (d.verdict === "uncertain" || d.verdict === "not_measured") uncertainties.push(`${d.label} : ${d.verdictLabel}${d.note ? ` — ${d.note}` : ""}`);
  }

  const w = num(s.measures?.image?.width);
  const h = num(s.measures?.image?.height);
  return {
    position: num(s.position),
    analysed,
    error: s.error ?? null,
    textStatus,
    textError: s.text?.error ?? null,
    size: w && h ? [w, h] : null,
    lines,
    composition,
    dominantColors: (s.measures?.dominant_colors ?? []).filter((c) => typeof c?.hex === "string"),
    bottomMinusMiddle: num(s.measures?.light?.bottom_minus_middle_l),
    ochreBlocks: blocks,
    detections,
    uncertainties,
  };
}

/** True when the version is known and older than [major, minor]. */
function olderThan(version: string | undefined, [major, minor]: [number, number]): boolean {
  const m = /^(\d+)\.(\d+)/.exec(version ?? "");
  return m ? Number(m[1]) < major || (Number(m[1]) === major && Number(m[2]) < minor) : false;
}

const normalised = (t: string) => t.normalize("NFD").replace(/\p{M}/gu, "").toUpperCase().replace(/[^A-Z0-9]/g, "");

/** The descriptive sheet of one stored analysis document. */
export function buildFiche(doc: AnalysisDocument): Fiche {
  const slides = (doc.slides ?? []).filter((s) => (s.role ?? "slide") === "slide").map(slide).sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const linesByKind: Record<string, number> = {};
  const repeated = new Map<string, { text: string; positions: Set<number> }>();
  const numbering: Fiche["numbering"] = [];
  for (const s of slides) {
    for (const l of s.lines) {
      linesByKind[l.kind] = (linesByKind[l.kind] ?? 0) + 1;
      if ((l.repeatedOn ?? 0) >= 2 && s.position !== null) {
        const key = `${l.kind}:${normalised(l.text)}`;
        if (!repeated.has(key)) repeated.set(key, { text: l.text, positions: new Set() });
        repeated.get(key)!.positions.add(s.position);
      }
      if (l.numbering && s.position !== null) numbering.push({ position: s.position, text: l.text });
    }
  }
  // what this version could not record: a field absent from every line or slide (not a value of none)
  const rawLines = (doc.slides ?? []).flatMap((s) => s.text?.lines ?? []);
  const rawSlides = (doc.slides ?? []).filter((s) => s.status === "analyzed");
  const has = (key: keyof AnalysisDocumentLine) => rawLines.some((l) => key in l);
  const missing: string[] = [];
  if (rawLines.length && !rawLines.some((l) => l.kind === "brand") && olderThan(doc.analyzer?.version, [0, 3])) missing.push("la marque (logo, compte) n'est pas séparée du texte ajouté");
  if (rawLines.length && !has("repeated_on")) missing.push("les répétitions d'une slide à l'autre et la numérotation ne sont pas relevées");
  if (rawLines.length && !has("coloured")) missing.push("les blocs et lettres de couleur ne sont pas mesurés ligne par ligne");
  if (rawSlides.length && !rawSlides.some((s) => s.measures?.composition)) missing.push("la composition en deux images n'est pas mesurée");
  return {
    analyzerVersion: doc.analyzer?.version ?? "inconnue",
    generatedAt: doc.generated_at ?? null,
    slides,
    linesByKind,
    repeated: [...repeated.values()].filter((r) => r.positions.size >= 2).map((r) => ({ text: r.text, positions: [...r.positions].sort((a, b) => a - b) })),
    numbering: numbering.sort((a, b) => a.position - b.position),
    missing,
    limits: [...(doc.limits ?? []), ...KNOWN_LIMITS],
  };
}
